// ActionButtons.jsx
import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";

// Default action configurations
const DEFAULT_META = {
  view: {
    label: "View",
    icon: <VisibilityIcon color="info" fontSize="small" />,
  },
  edit: { label: "Edit", icon: <EditIcon color="action" fontSize="small" /> },
  delete: {
    label: "Delete",
    icon: <DeleteIcon color="error" fontSize="small" />,
  },
};

const ActionButtons = ({
  id,
  actions = ["view"],
  orientation = "horizontal",
  onAction,
  spacing = 0.5,
  iconSize = "small",
  actionMeta = {},
}) => {
  // Merge default meta with custom overrides
  const mergedMeta = useMemo(
    () => ({ ...DEFAULT_META, ...actionMeta }),
    [actionMeta]
  );

  // Ensure view is always present and filter valid actions
  const allowedActions = useMemo(() => {
    const uniqueActions = Array.from(new Set(["view", ...actions]));
    return uniqueActions.filter((action) => mergedMeta[action]);
  }, [actions, mergedMeta]);

  const handleAction = useCallback(
    (action) => {
      if (onAction) onAction(action, id);
    },
    [onAction, id]
  );

  // Menu state management
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const openMenu = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const closeMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Menu variant
  if (orientation === "menu") {
    return (
      <>
        <Tooltip title="Actions">
          <IconButton
            size={iconSize}
            onClick={openMenu}
            aria-label="Action menu"
            aria-controls={open ? "action-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <MoreVertIcon fontSize={iconSize} />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={closeMenu}
          id="action-menu"
          MenuListProps={{
            "aria-labelledby": "action-menu-button",
          }}
        >
          {allowedActions.map((action) => {
            const { label, icon } = mergedMeta[action];
            return (
              <MenuItem
                key={action}
                onClick={() => {
                  handleAction(action);
                  closeMenu();
                }}
              >
                <ListItemIcon>{icon}</ListItemIcon>
                <ListItemText primary={label} />
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  }

  // Stacked buttons variant
  return (
    <Stack
      direction={orientation === "vertical" ? "column" : "row"}
      spacing={spacing}
      aria-label="Row actions"
    >
      {allowedActions.map((action) => {
        const { label, icon } = mergedMeta[action];
        return (
          <Tooltip key={action} title={label}>
            <IconButton
              size={iconSize}
              onClick={() => handleAction(action)}
              aria-label={label.toLowerCase()}
            >
              {icon}
            </IconButton>
          </Tooltip>
        );
      })}
    </Stack>
  );
};

ActionButtons.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  actions: PropTypes.arrayOf(PropTypes.string),
  orientation: PropTypes.oneOf(["horizontal", "vertical", "menu"]),
  onAction: PropTypes.func,
  spacing: PropTypes.number,
  iconSize: PropTypes.oneOf(["small", "medium", "large"]),
  actionMeta: PropTypes.shape({
    view: PropTypes.shape({
      label: PropTypes.string,
      icon: PropTypes.node,
    }),
    edit: PropTypes.shape({
      label: PropTypes.string,
      icon: PropTypes.node,
    }),
    delete: PropTypes.shape({
      label: PropTypes.string,
      icon: PropTypes.node,
    }),
    // Allows custom actions
    [PropTypes.string]: PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.node.isRequired,
    }),
  }),
};

export default ActionButtons;
